import React from "react";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DownloadIcon from "@mui/icons-material/Download";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import RoleplayAttemptReviewModal from "./RoleplayAttemptReviewModal";

function RoleplayRecordTable({ record, onBack, employeeAvatarColorByUserId, homeCompany }) {
  const [reviewSubmission, setReviewSubmission] = React.useState(null);

  const submissions = record.submissions || [];
  const incompleteAssignments = record.incomplete_assignments || [];

  const hasSubmissions = submissions.length > 0;
  const hasIncomplete = incompleteAssignments.length > 0;
  const hasData = hasSubmissions || hasIncomplete;

  const formatDateReadable = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportRoleplayCSV = () => {
    const rows = [];
    submissions.forEach((s) => {
      rows.push({
        Employee: s.employee_name,
        Attempts: s.attempt_count,
        Met: s.has_scorecard ? s.met_count : "",
        Partial: s.has_scorecard ? s.partial_count : "",
        Missed: s.has_scorecard ? s.missed_count : "",
        Replies: s.turns_used,
        Date: s.date_completed ? formatDateReadable(s.date_completed) : "",
      });
    });
    incompleteAssignments.forEach((a) => {
      rows.push({
        Employee: a.employee_name,
        Attempts: "",
        Met: "",
        Partial: "",
        Missed: "",
        Replies: "",
        Date: "Not completed",
      });
    });
    if (rows.length === 0) return;
    downloadCSV(
      objectToCSV(rows),
      `practice_scenario_${(record.name || "practice_scenario").replace(/\s+/g, "_")}.csv`
    );
  };

  return (
    <div className="px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium mb-4 transition-colors"
        >
          <ChevronLeftIcon className="-ml-2" style={{ fontSize: "24px" }} />
          Back to Records
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              {record.name}
            </h2>
            <p className="text-sm text-slate-600">
              Practice conversations: coaching results, not pass/fail
            </p>
          </div>
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
            onClick={exportRoleplayCSV}
          >
            <DownloadIcon style={{ fontSize: "20px" }} />
            <span className="font-medium">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Details card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Success Criteria
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.criteria_count ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Max Replies
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.max_turns ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Completions
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {record.total_submissions} / {record.assigned_count}
            </div>
          </div>
        </div>
      </div>

      {/* Attempts table */}
      <div className="-mx-6 border-y border-slate-200 bg-white">
        {hasData ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Coaching Results
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Attempts
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {submissions.map((s, index) => (
                  <tr
                    key={`done-${index}`}
                    onClick={() => setReviewSubmission(s)}
                    className="hover:bg-slate-50 transition-colors duration-150 cursor-pointer"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={s.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[s.employee_id] ||
                              "bg-slate-600"
                            }
                          />
                        </div>
                        <div className="text-xs font-medium text-slate-900">
                          {s.employee_name}
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      {s.has_scorecard ? (
                        <div className="flex items-center gap-1.5">
                          {s.met_count > 0 && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                              {s.met_count} met
                            </span>
                          )}
                          {s.partial_count > 0 && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                              {s.partial_count} partial
                            </span>
                          )}
                          {s.missed_count > 0 && (
                            <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-xs font-medium rounded">
                              {s.missed_count} missed
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">
                          Completed · no feedback
                        </span>
                      )}
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <span className="text-xs text-slate-600">
                        {s.attempt_count} attempt{s.attempt_count === 1 ? "" : "s"}
                        {" · "}
                        {s.turns_used} repl{s.turns_used === 1 ? "y" : "ies"}
                      </span>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <CalendarTodayIcon style={{ fontSize: "13px" }} />
                        {s.date_completed ? formatDateReadable(s.date_completed) : "—"}
                      </div>
                    </td>
                  </tr>
                ))}
                {incompleteAssignments.map((a, index) => (
                  <tr
                    key={`todo-${index}`}
                    className="hover:bg-slate-50 transition-colors duration-150"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={a.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[a.employee_id] ||
                              "bg-slate-600"
                            }
                          />
                        </div>
                        <div className="text-xs font-medium text-slate-900">
                          {a.employee_name}
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3" colSpan={3}>
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                        Not completed
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <CheckCircleIcon
              className="mx-auto mb-3 text-slate-300"
              style={{ fontSize: "28px" }}
            />
            <p className="text-slate-400 text-sm">No assignments yet.</p>
          </div>
        )}
      </div>

      {reviewSubmission && (
        <RoleplayAttemptReviewModal
          open={!!reviewSubmission}
          onClose={() => setReviewSubmission(null)}
          homeCompany={homeCompany}
          attemptUuid={reviewSubmission.attempt_uuid}
          storeTeamMemberId={reviewSubmission.store_team_member_id}
          employeeName={reviewSubmission.employee_name}
        />
      )}
    </div>
  );
}

export default RoleplayRecordTable;
